import type { RegisterRequest } from '@/queries/auth/auth.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useFingerprintRequestIdNoCache } from '@/components/entities/analytics/components/FingerprintProvider/hooks/fingerPrintRequestId';
import { UserDeviceActivityType } from '@/components/entities/analytics/components/FingerprintProvider/types/fingerPrintType';
import { pushCustomGtmEventToDataLayer, pushToDataLayer } from '@/components/entities/analytics/gtm';
import { ExternalAuthButtons } from '@/components/entities/auth/ExternalAuthButtons';
import { Icon } from '@/components/entities/Sprite';
import { Button } from '@/components/ui/Button/button';
import { Input } from '@/components/ui/Input/input';
import Toast from '@/components/ui/Toast/toast';
import { getSeonSessionId } from '@/libs/analytics/seon';
import { clearUsedAffiliateTokenSource } from '@/libs/userSource/affiliateTokenSource';
import { readCustomerSourceHeadersFromStorage } from '@/libs/userSource/customerSourceStorage';
import { parseUserSourceHeaders } from '@/libs/userSource/parseUserSourceHeaders';
import { isWeb2AppAttributionFlow, markWeb2AppGcPending } from '@/libs/userSource/web2app-campaign';
import openModal from '@/modals/utils/openModal';
import { authApi } from '@/queries/auth/auth.api';
import { RegisterRequestSchema } from '@/queries/auth/auth.schema';
import { APIError } from '@/queries/httpClient';
import { cn } from '@/utils/cn';
import { useResendVerification } from '../../hooks/useResendVerification';
import { RegisterStep } from '../type';
import { setFirstVisitRegistration } from '../visitRegistration.utils';
import { getPendingRegistrationEmail, setPendingRegistration } from './registrationSession.utils';

interface RegisterPopupFormProps {
  onRegistrationFailureAction?: () => void;
  onRegistrationStartAction?: () => void;
  setStepOfRegistration: (step: RegisterStep) => void;
  setEmail: (email: string) => void;
}

export const RegisterPopupForm = ({
  onRegistrationFailureAction,
  onRegistrationStartAction,
  setStepOfRegistration,
  setEmail,
}: RegisterPopupFormProps) => {
  const t = useTranslations('register');
  const { mutation, timer } = useResendVerification();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    watch,
  } = useForm<RegisterRequest & {
  }>({
    resolver: zodResolver(RegisterRequestSchema),
    defaultValues: {
      email: getPendingRegistrationEmail() || '',
      password: '',
      languageCode: 'en',
    },
    mode: 'onSubmit',
  });

  const email = watch('email');
  const password = watch('password');
  const hasPendingRegistration = getPendingRegistrationEmail() !== ''
    && email.trim().toLowerCase() === getPendingRegistrationEmail()?.toLowerCase();

  const isFormValid = useMemo(() => {
    return (
      email.trim() !== ''
      && password.trim() !== ''
    );
  }, [email, password]);

  const { getFingerprintRequestIdNoCache } = useFingerprintRequestIdNoCache({ activityType: UserDeviceActivityType.Signup });

  const headers = async () => {
    const storedCustomerSourceHeaders = readCustomerSourceHeadersFromStorage();
    const parsedCustomerSourceHeaders = Object.keys(storedCustomerSourceHeaders).length
      ? storedCustomerSourceHeaders
      : parseUserSourceHeaders(window.location.href);
    const requestId = await getFingerprintRequestIdNoCache();

    const sessionId = await getSeonSessionId();

    // static headers
    const staticHeaders: Record<string, string | undefined> = {
      'x-fingerprint-request-id': requestId,
      'x-anti-fraud-session-id': sessionId,
    };

    return Object.entries({
      ...parsedCustomerSourceHeaders,
      ...staticHeaders,
    }).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, string>);
  };

  const onSubmit = async () => {
    try {
      if (!hasPendingRegistration) {
        onRegistrationStartAction?.();
      }

      const requestHeaders = await headers();

      const { id } = await authApi.register({
        email,
        password,
        languageCode: 'en',
      }, requestHeaders);
      clearUsedAffiliateTokenSource();

      if (isWeb2AppAttributionFlow()) {
        markWeb2AppGcPending();
      }

      setPendingRegistration(email, id);

      // FYI: We need to set first visit registration to true, to open login modal,
      // if user visited another tab and return to the site
      setFirstVisitRegistration(true);

      pushToDataLayer('register_start');
      pushCustomGtmEventToDataLayer({
        eventName: 'user_created',
        auth_source: 'email',
        user: {
          email,
          id,
        },
      });

      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'register_start', {
          email,
        });
      }

      setEmail(email);
      setStepOfRegistration(RegisterStep.COMPLETE_REGISTRATION);
    } catch (error) {
      onRegistrationFailureAction?.();

      if (error instanceof APIError && error.status === 409) {
        Toast({
          type: 'error',
          title: t('registration-failed'),
          description: t('email-error'),
        });
      } else {
        console.error(error);
      }
    }
  };

  const handleResetVerification = () => mutation.mutate(email);

  const disableButton = timer.isDisabled || mutation.isPending || email.trim() === '';
  const buttonVariantText = timer.count > 0 ? `${t('resend-code-in')} 0:${String(timer.count).padStart(2, '0')}` : `${t('resend-verification-email')}`;

  return (
    <div className="flex h-full">
      <form id="register-form" onSubmit={handleSubmit(onSubmit)} className="w-full h-full flex flex-col justify-between">

        <div className="flex items-center gap-2.5 lg:hidden">
          <div className="border border-c300/15 flex-1" />
          <div className="relative">
            <Icon name="age-registration" className="size-16 text-primary-1" />
            <span className="text-white absolute
              top-1/2 left-1/2 -translate-x-1/2
              -translate-y-1/2 font-hepta font-extrabold text-[1.25rem] leading-[100%] tracking-[-1px]
              "
            >
              18+
            </span>
          </div>
          <div className="border border-c300/15 flex-1" />
        </div>
        <ExternalAuthButtons
          className="flex flex-col gap-3 max-lg:mt-4"
          getFingerprintRequestIdNoCache={getFingerprintRequestIdNoCache}
        />

        <div className="flex items-center gap-2.5 py-4">
          <div className="border border-c300/15 flex-1" />
          <span className="paragraph-s-regular text-c200">{t('or')}</span>
          <div className="border border-c300/15 flex-1" />
        </div>

        <div className="flex flex-col">
          <Input
            {...register('email', { required: t('email-required') })}
            type="email"
            labelClassName="text-white paragraph-s-regular"
            label={t('email')}
            placeholder={t('email-placeholder')}
            error={{
              isError: !!errors.email,
              errorText: errors.email?.message || '',
            }}
          />
          <div className="flex flex-col gap-2 mt-4">
            <Input
              {...register('password', { required: t('password-required') })}
              type="password"
              labelClassName="text-white paragraph-s-regular"
              label={t('password')}
              placeholder={t('password-placeholder')}
              error={{
                isError: !!errors.password,
              }}
            />

            {hasPendingRegistration
              ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        className={cn('text-c300 text-sm leading-5 font-regular flex items-center', timer.count > 0 ? 'text-c300 cursor-default' : 'text-white cursor-pointer')}
                        disabled={disableButton}
                        onClick={handleResetVerification}
                      >
                        {buttonVariantText}

                      </button>
                      {mutation.isPending && <Icon name="loading" className="size-4 animate-spin shrink-0" />}
                    </div>
                    <div className="p-2 bg-c300/10 rounded-xl flex gap-2 mt-1.5">
                      <Icon name="info-payment" className="size-4 shrink-0 text-c300" />
                      <div className="flex items-center gap-1.5">
                        <p className="text-c300 font-regular text-[0.75rem] leading-4">
                          {t('you-have-already-registered-but-you-havent-confirmed-your-email-yet')}
                        </p>
                      </div>
                    </div>
                  </>
                )
              : (
                  <p className={cn('paragraph-s-regular', errors.password ? 'text-main-error' : 'text-c300')}>
                    {t('password-must-contain-at-least-8-characters')}
                  </p>
                )}

          </div>

        </div>

        <div className="mt-auto">
          <Button
            type="submit"
            variant="primary"
            className="w-full py-3 mt-6"
            disabled={!isFormValid || isSubmitting}
            isLoading={isSubmitting}
          >
            {t('title')}
          </Button>

          <span className="flex items-center gap-1 text-sm justify-center w-full mt-6 lg:mt-4">
            <p className="text-c200 paragraph-s-medium">{t('already-registered')}</p>
            <button
              type="button"
              className="text-primary-1 paragraph-s-medium hover:text-white transition-colors duration-300 cursor-pointer"
              onClick={() => openModal('login')}
            >
              {t('sign-in')}
            </button>
          </span>

        </div>
      </form>
    </div>
  );
};
